import type { CsvProblem, RowToSkip } from '../types/whatsapp';
import { pluralize } from '../utils/pluralize';
import CsvProblemList from './CsvProblemList';
import RowsToSkipTable from './RowsToSkipTable';

const PASSED_STYLE = 'bg-green-50 text-green-700';
const WARNING_STYLE = 'bg-amber-50 text-amber-800';
const BLOCKED_STYLE = 'bg-red-50 text-red-700';

interface SendCheckSummaryProps {
  sheetProblems: CsvProblem[];
  sendCount: number;
  rowsToSkip: RowToSkip[];
}

const SEND_AGAIN_WARNING =
  'To reach the skipped people later, send a new sheet with only those rows. Sending this sheet again sends to everyone twice.';

function Notice({
  style,
  text,
  detail,
}: {
  style: string;
  text: string;
  detail?: string;
}) {
  return (
    <div className={`space-y-1 rounded-md px-4 py-2 text-sm ${style}`}>
      <p>{text}</p>
      {detail && <p>{detail}</p>}
    </div>
  );
}

function SkipNotice({
  sendCount,
  skipCount,
}: {
  sendCount: number;
  skipCount: number;
}) {
  const skipped = pluralize(skipCount, 'row', 'rows');
  if (sendCount === 0) {
    return (
      <Notice
        style={BLOCKED_STYLE}
        text={`None of the rows can be sent. Fix the ${skipped} below in your sheet and add it again.`}
      />
    );
  }

  return (
    <Notice
      style={WARNING_STYLE}
      text={`${skipped} will be skipped. This message goes to ${pluralize(sendCount, 'person', 'people')}.`}
      detail={SEND_AGAIN_WARNING}
    />
  );
}

export default function SendCheckSummary({
  sheetProblems,
  sendCount,
  rowsToSkip,
}: SendCheckSummaryProps) {
  if (sheetProblems.length > 0) {
    return <CsvProblemList problems={sheetProblems} />;
  }
  if (rowsToSkip.length === 0) {
    return (
      <Notice
        style={PASSED_STYLE}
        text={`All checks passed. This message goes to ${pluralize(sendCount, 'person', 'people')}.`}
      />
    );
  }

  return (
    <div className="space-y-4" role="status">
      <SkipNotice sendCount={sendCount} skipCount={rowsToSkip.length} />
      <RowsToSkipTable rowsToSkip={rowsToSkip} />
    </div>
  );
}
